import { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router';
import SEOHead from '@/components/SEOHead';
import { NeobrutalButton } from '@/components/marketing/v2/NeobrutalButton';
import { FinalCtaSection } from '@/components/marketing/v2/FinalCtaSection';
import BlogPostHeroV2 from '@/components/marketing/blog/v2/BlogPostHeroV2';
import BlogPostTocV2 from '@/components/marketing/blog/v2/BlogPostTocV2';
import BlogPostRelatedV2 from '@/components/marketing/blog/v2/BlogPostRelatedV2';
import { renderArticle } from '@/components/marketing/blog/v2/markdown';
import { authorJsonLd, PUBLISHER_JSONLD, resolveAuthor } from '@/lib/seo';

interface BlogArticle {
  slug: string;
  title: string;
  description: string;
  date: string;
  author: string;
  tags: string[];
  image?: string;
  cover?: string;
  content: string;
}

interface BlogEntry {
  slug: string;
  title: string;
  description: string;
  date: string;
  author: string;
  tags: string[];
  image?: string;
}

export default function BlogPost() {
  const { slug } = useParams<{ slug: string }>();
  const [article, setArticle] = useState<BlogArticle | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [related, setRelated] = useState<BlogEntry[]>([]);

  useEffect(() => {
    if (!slug) return;
    setArticle(null);
    setNotFound(false);
    fetch(`/blog/${slug}.json`)
      .then((r) => {
        if (!r.ok) throw new Error('Not found');
        return r.json();
      })
      .then((data: BlogArticle) => setArticle(data))
      .catch(() => setNotFound(true));

    fetch('/blog/index.json')
      .then((r) => r.json())
      .then((data: BlogEntry[]) => setRelated(data.filter((a) => a.slug !== slug).slice(0, 3)))
      .catch(() => setRelated([]));
  }, [slug]);

  const parsed = useMemo(() => {
    if (!article) return null;
    return renderArticle(article.content);
  }, [article]);

  const readMinutes = useMemo(() => {
    if (!article) return 0;
    const words = article.content.split(/\s+/).length;
    return Math.max(3, Math.round(words / 220));
  }, [article]);

  if (notFound) {
    return (
      <section className="bg-paper py-32">
        <div className="mx-auto max-w-2xl px-6 text-center md:px-8">
          <h1 className="font-marketing-display text-[clamp(40px,5vw,64px)] font-bold tracking-[-0.04em] text-charcoal-800">
            Article introuvable.
          </h1>
          <p className="mt-4 font-marketing-body text-lg text-charcoal-600">
            Cet article n'existe pas ou a été déplacé.
          </p>
          <div className="mt-8 inline-flex">
            <NeobrutalButton as="link" to="/blog" variant="primary" size="md">
              Retour au blog
            </NeobrutalButton>
          </div>
        </div>
      </section>
    );
  }

  if (!article || !parsed) {
    return (
      <section className="bg-paper py-32">
        <div className="mx-auto max-w-3xl space-y-4 px-6 md:px-8">
          <div className="h-6 w-32 animate-pulse border-2 border-charcoal-200 bg-charcoal-100" />
          <div className="h-14 w-full animate-pulse border-2 border-charcoal-200 bg-charcoal-100" />
          <div className="h-5 w-full animate-pulse border-2 border-charcoal-200 bg-charcoal-100" />
          <div className="h-5 w-3/4 animate-pulse border-2 border-charcoal-200 bg-charcoal-100" />
        </div>
      </section>
    );
  }

  const author = resolveAuthor(article.author);

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: article.title,
    description: article.description,
    datePublished: article.date,
    dateModified: article.date,
    author: authorJsonLd(author),
    publisher: PUBLISHER_JSONLD,
    mainEntityOfPage: `https://velko-pos.com/blog/${article.slug}`,
    ...(article.image && { image: `https://velko-pos.com${article.image}` }),
  };

  return (
    <>
      <SEOHead
        title={`${article.title} — Blog Velko POS`}
        description={article.description}
        path={`/blog/${article.slug}`}
        ogImage={article.image}
        jsonLd={articleJsonLd}
        breadcrumbs={[
          { name: 'Accueil', path: '/' },
          { name: 'Blog', path: '/blog' },
          { name: article.title, path: `/blog/${article.slug}` },
        ]}
      />

      <BlogPostHeroV2
        title={article.title}
        description={article.description}
        date={article.date}
        author={author.name}
        tags={article.tags}
        readMinutes={readMinutes}
        cover={article.cover}
      />

      <section className="bg-paper py-12 md:py-16">
        <div className="mx-auto max-w-7xl px-6 md:px-8">
          <div className="grid gap-12 md:grid-cols-[0.9fr_2.1fr] md:gap-16">
            <BlogPostTocV2 headings={parsed.headings} />

            {/* Content is rendered from trusted first-party static JSON files (public/blog/*.json). */}
            <article
              className="min-w-0 max-w-3xl"
              dangerouslySetInnerHTML={{ __html: parsed.html }}
            />
          </div>

          {/* Author card */}
          <div className="mx-auto mt-16 max-w-3xl md:ml-[calc((0.9/3)*100%+4rem)]">
            <div className="flex flex-col items-start gap-5 border-2 border-charcoal-800 bg-white p-6 shadow-neobrutal-6 md:flex-row md:items-center md:p-7">
              <div className="flex h-14 w-14 flex-shrink-0 items-center justify-center border-2 border-charcoal-800 bg-lime-500 font-marketing-display text-xl font-bold text-charcoal-800 shadow-neobrutal-2">
                {author.name.slice(0, 1)}
              </div>
              <div className="flex-1">
                <div className="font-marketing-mono text-[11px] font-medium uppercase tracking-[0.14em] text-charcoal-500">
                  {author.role}
                </div>
                <div className="mt-1 font-marketing-display text-[18px] font-bold text-charcoal-800">
                  {author.name}
                </div>
                <p className="mt-2 font-marketing-body text-[14.5px] leading-relaxed text-charcoal-600">
                  {author.bio}
                </p>
              </div>
              <Link
                to="/blog"
                className="inline-flex items-center gap-2 border-2 border-charcoal-800 bg-paper px-4 py-2 font-marketing-display text-[13px] font-bold text-charcoal-800 shadow-neobrutal-2 transition-transform hover:-translate-x-[1px] hover:-translate-y-[1px] hover:shadow-neobrutal-4"
              >
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                >
                  <path d="M19 12H5" />
                  <path d="M11 6l-6 6 6 6" />
                </svg>
                Tous les articles
              </Link>
            </div>
          </div>
        </div>
      </section>

      <BlogPostRelatedV2 items={related} />

      <FinalCtaSection
        eyebrow="Passe à l'action"
        title="Essaie Velko"
        highlight="gratuitement."
        subtitle="Les 8 modules, le mode offline, Orange / MTN / Wave. Sans CB. Sans engagement. Prêt en 3 minutes."
        primaryLabel="Créer mon compte"
        primaryTo="/register"
        secondaryLabel="Parler à un humain"
        secondaryTo="/contact"
        guarantees={['14 jours d’essai', 'Sans CB', 'Support FR 24/7']}
      />
    </>
  );
}
